import React from 'react';
import { X, Mail, User, MapPin, Briefcase } from 'lucide-react';
import { Agent } from '../../../types/agent';
import { detectLanguage } from '../../../utils/languageUtils';
import { motion } from 'framer-motion';
import { cn } from '../../../utils/cn';

interface AgentProfilePopupProps {
  agent: Agent;
  onClose: () => void;
}

export function AgentProfilePopup({ agent, onClose }: AgentProfilePopupProps) {
  const language = detectLanguage();
  
  // Ensure the close handler properly stops propagation
  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with background color */}
        <div className="bg-gradient-to-r from-brand-600 to-brand-500 h-36 relative">
          {/* Fixed close button with explicit onClick handler */}
          <button 
            type="button"
            onClick={handleClose}
            className="absolute top-3 right-3 text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1 transition-colors z-10"
            aria-label="Close profile"
          >
            <X size={24} />
          </button>
          
          {/* Decorative elements */}
          <div className="absolute inset-0 overflow-hidden opacity-20">
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/20"></div>
            <div className="absolute -left-10 -bottom-10 w-32 h-32 rounded-full bg-white/10"></div>
          </div>
        </div>
        
        {/* Profile image - positioned to overlap header and content */}
        <div className="flex justify-center -mt-20">
          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="relative"
          >
            <img 
              src={agent.avatarUrl} 
              alt={agent.name}
              className="w-36 h-36 rounded-full border-4 border-white object-cover shadow-lg"
            />
            <div className="absolute bottom-3 right-3 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
          </motion.div>
        </div>
        
        {/* Profile content */}
        <div className="p-6 pt-4">
          {/* Name and role */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">{agent.name}</h2>
            <p className="text-brand-500 font-medium">{agent.role}</p>
          </div>
          
          {/* Contact and info */}
          <div className="space-y-5 mb-6">
            <ProfileInfoItem 
              icon={<Mail className="text-brand-500" size={20} />}
              label={language === 'nl' ? 'E-mail' : 'Email'}
              value={agent.email}
            />
            
            <ProfileInfoItem 
              icon={<Briefcase className="text-brand-500" size={20} />}
              label={language === 'nl' ? 'Bij het bedrijf sinds' : 'At the company for'}
              value={language === 'nl' 
                ? `${agent.yearsAtCompany} jaar` 
                : `${agent.yearsAtCompany} years`}
            />
            
            <ProfileInfoItem 
              icon={<MapPin className="text-brand-500" size={20} />}
              label={language === 'nl' ? 'Locatie' : 'Location'}
              value="Assendelft, Nederland"
            />
            
            <ProfileInfoItem 
              icon={<User className="text-brand-500" size={20} />}
              label={language === 'nl' ? 'Over mij' : 'About me'}
              value={agent.bio}
              isLong
            />
          </div>
          
          {/* Close button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleClose}
            className="w-full py-2.5 bg-gradient-to-r from-brand-600 to-brand-500 text-white rounded-lg hover:from-brand-700 hover:to-brand-600 transition-colors font-medium"
          >
            {language === 'nl' ? 'Sluiten' : 'Close'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

interface ProfileInfoItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  isLong?: boolean;
}

function ProfileInfoItem({ icon, label, value, isLong = false }: ProfileInfoItemProps) {
  return (
    <div className={cn("flex", isLong ? "items-start" : "items-center")}>
      <div className="mr-3 mt-1">{icon}</div>
      <div>
        <p className="text-sm text-gray-500 mb-1 font-medium">
          {label}
        </p>
        <p className={cn(
          "text-gray-800", 
          isLong ? "leading-relaxed" : ""
        )}>
          {value}
        </p>
      </div>
    </div>
  );
}